/**
 * SensitiveText Component - Higher Order Component for Sensitive Data
 *
 * Wraps sensitive information and applies masking/blurring based on privacy settings.
 * Supports multiple data types with customizable masking patterns.
 *
 * @example
 * <SensitiveText type="email">{employee.email}</SensitiveText>
 * <SensitiveText type="salary">R$ 12.500,00</SensitiveText>
 * <SensitiveText type="phone">{employee.phone}</SensitiveText>
 */

import React from 'react'
import { cn } from '@/lib/utils'
import { useSensitiveDataHidden } from '@/stores/privacyStore'

export type SensitiveDataType =
  | 'email'
  | 'phone'
  | 'address'
  | 'salary'
  | 'currency'
  | 'cpf'
  | 'bankAccount'
  | 'name'
  | 'revenue'

interface SensitiveTextProps {
  children: React.ReactNode
  type: SensitiveDataType
  /**
   * Custom mask pattern to display instead of default
   */
  maskPattern?: string
  /**
   * Use blur effect instead of character masking
   */
  blur?: boolean
  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Get default mask pattern for a given data type
 */
const getDefaultMask = (type: SensitiveDataType, value: string): string => {
  switch (type) {
    case 'email':
      // Mask: j***@example.com
      if (value && value.includes('@')) {
        const [local, domain] = value.split('@')
        return `${local[0]}***@${domain}`
      }
      return '***@***.***'

    case 'phone':
      // Mask: (11) ••••-1234 (show last 2 digits)
      if (value && value.length >= 4) {
        const lastTwo = value.slice(-2)
        return value.replace(/\d(?=\d{2})/g, '•')
      }
      return '(••) ••••-••••'

    case 'salary':
    case 'currency':
    case 'revenue':
      // Full redaction for financial data
      return 'R$ ••••••'

    case 'cpf':
      // Mask: •••.•••.•••-••
      return '•••.•••.•••-••'

    case 'bankAccount':
      // Mask: ••••••••
      return '••••••••'

    case 'address':
      // Comprehensive mask for addresses
      // Show only state (if identifiable), mask street/building/ZIP
      if (value && value.includes(',')) {
        const parts = value.split(',').map(p => p.trim())
        // If we have at least 2 parts, show only state (usually 2nd to last)
        if (parts.length >= 2) {
          // Get the state part (usually contains 2-letter state code)
          const statePart = parts[parts.length - 2]
          // Check if it looks like a Brazilian state (2 letters) or state name
          const stateMatch = statePart.match(/\b([A-Z]{2})\b/)
          if (stateMatch) {
            return `•••••••••, ${stateMatch[1]}, •••••-•••`
          }
          // Fallback: show last part but mask others
          return `•••••••••, ${parts[parts.length - 1]}`
        }
      }
      return '••••••••'

    case 'name':
      // For names, we'll use blur instead
      return value

    default:
      return '••••••'
  }
}

/**
 * SensitiveText Component
 */
export const SensitiveText: React.FC<SensitiveTextProps> = ({
  children,
  type,
  maskPattern,
  blur = false,
  className,
}) => {
  const isSensitiveHidden = useSensitiveDataHidden()

  // If not hidden, render children as-is
  if (!isSensitiveHidden) {
    return <>{children}</>
  }

  const childText = typeof children === 'string' ? children : String(children || '')

  // Use blur effect for certain types (names, images)
  const shouldBlur = blur || type === 'name'

  if (shouldBlur) {
    return (
      <span
        className={cn(
          'transition-all duration-300 blur-sm select-none',
          className
        )}
        aria-label="Hidden for privacy"
      >
        {children}
      </span>
    )
  }

  // Use masking pattern
  const maskedValue = maskPattern || getDefaultMask(type, childText)

  return (
    <span
      className={cn(
        'text-brand-gray tracking-wider transition-opacity duration-300',
        className
      )}
      aria-label={`Hidden ${type}`}
      title="Hidden for privacy"
    >
      {maskedValue}
    </span>
  )
}

/**
 * Alternative: SensitiveValue - For already-formatted values
 * Use when you want to mask a pre-formatted currency/phone value
 */
interface SensitiveValueProps {
  children: React.ReactNode
  /**
   * Mask to show when hidden
   */
  mask: string
  /**
   * Use blur instead of mask
   */
  blur?: boolean
  className?: string
}

export const SensitiveValue: React.FC<SensitiveValueProps> = ({
  children,
  mask,
  blur = false,
  className,
}) => {
  const isSensitiveHidden = useSensitiveDataHidden()

  if (!isSensitiveHidden) {
    return <>{children}</>
  }

  if (blur) {
    return (
      <span
        className={cn(
          'transition-all duration-300 blur-sm select-none',
          className
        )}
        aria-label="Hidden for privacy"
      >
        {children}
      </span>
    )
  }

  return (
    <span
      className={cn(
        'text-brand-gray tracking-wider transition-opacity duration-300',
        className
      )}
      aria-label="Hidden value"
      title="Hidden for privacy"
    >
      {mask}
    </span>
  )
}
